import React, { useState, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const ActivityHistory = ({ user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d');

  // Mock activity data
  const [activities] = useState([
    {
      id: 'act-001',
      type: 'login',
      title: 'Anmeldung',
      description: 'Chrome auf Windows (Berlin, Deutschland)',
      timestamp: '2025-01-05T10:00:00Z',
      icon: 'LogIn',
      status: 'success',
      metadata: {
        device: 'Chrome auf Windows',
        location: 'Berlin, Deutschland',
        ip: '192.168.1.100'
      }
    },
    {
      id: 'act-002',
      type: 'agent_created',
      title: 'AI-Agent erstellt',
      description: 'Neuer Agent "Kundenservice Bot" wurde erstellt',
      timestamp: '2025-01-05T09:45:00Z',
      icon: 'Plus',
      status: 'success',
      metadata: {
        agentName: 'Kundenservice Bot',
        agentId: 'agent-001'
      }
    },
    {
      id: 'act-003',
      type: 'profile_updated',
      title: 'Profil aktualisiert',
      description: 'Persönliche Informationen wurden geändert',
      timestamp: '2025-01-05T09:30:00Z',
      icon: 'Edit',
      status: 'success',
      metadata: {
        fields: ['firstName', 'bio']
      }
    },
    {
      id: 'act-004',
      type: 'api_key_generated',
      title: 'API-Schlüssel generiert',
      description: 'Neuer API-Schlüssel "Production Key" wurde erstellt',
      timestamp: '2025-01-04T16:20:00Z',
      icon: 'Key',
      status: 'success',
      metadata: {
        keyName: 'Production Key',
        permissions: ['read', 'write']
      }
    },
    {
      id: 'act-005',
      type: 'password_changed',
      title: 'Passwort geändert',
      description: 'Anmelde-Passwort wurde erfolgreich geändert',
      timestamp: '2025-01-04T14:15:00Z',
      icon: 'Lock',
      status: 'success',
      metadata: {}
    },
    {
      id: 'act-006',
      type: 'login_failed',
      title: 'Fehlgeschlagene Anmeldung',
      description: 'Safari auf iPhone (Berlin, Deutschland)',
      timestamp: '2025-01-04T08:30:00Z',
      icon: 'AlertCircle',
      status: 'warning',
      metadata: {
        device: 'Safari auf iPhone',
        location: 'Berlin, Deutschland',
        ip: '192.168.1.101',
        reason: 'Falsches Passwort'
      }
    },
    {
      id: 'act-007',
      type: '2fa_enabled',
      title: '2FA aktiviert',
      description: 'Zwei-Faktor-Authentifizierung wurde aktiviert',
      timestamp: '2025-01-03T19:45:00Z',
      icon: 'Shield',
      status: 'success',
      metadata: {}
    },
    {
      id: 'act-008',
      type: 'agent_deleted',
      title: 'AI-Agent gelöscht',
      description: 'Agent "Test Bot" wurde gelöscht',
      timestamp: '2025-01-03T15:20:00Z',
      icon: 'Trash2',
      status: 'warning',
      metadata: {
        agentName: 'Test Bot',
        agentId: 'agent-test-001'
      }
    },
    {
      id: 'act-009',
      type: 'login',
      title: 'Anmeldung',
      description: 'Firefox auf MacOS (München, Deutschland)',
      timestamp: '2025-01-03T14:10:00Z',
      icon: 'LogIn',
      status: 'success',
      metadata: {
        device: 'Firefox auf MacOS',
        location: 'München, Deutschland',
        ip: '10.0.0.50'
      }
    },
    {
      id: 'act-010',
      type: 'email_changed',
      title: 'E-Mail-Adresse geändert',
      description: 'E-Mail-Adresse wurde aktualisiert und verifiziert',
      timestamp: '2025-01-02T11:30:00Z',
      icon: 'Mail',
      status: 'success',
      metadata: {
        oldEmail: 'old@example.com',
        newEmail: 'max.mustermann@example.com'
      }
    }
  ]);

  const activityFilters = [
    { key: 'all', label: 'Alle Aktivitäten', count: activities?.length },
    { key: 'login', label: 'Anmeldungen', count: activities?.filter(a => a?.type === 'login')?.length },
    { key: 'security', label: 'Sicherheit', count: activities?.filter(a => ['password_changed', '2fa_enabled', 'api_key_generated']?.includes(a?.type))?.length },
    { key: 'agents', label: 'AI-Agents', count: activities?.filter(a => ['agent_created', 'agent_deleted', 'agent_updated']?.includes(a?.type))?.length },
    { key: 'profile', label: 'Profil', count: activities?.filter(a => ['profile_updated', 'email_changed']?.includes(a?.type))?.length }
  ];

  const dateRangeOptions = [
    { key: '24h', label: 'Letzte 24 Stunden' },
    { key: '7d', label: 'Letzte 7 Tage' },
    { key: '30d', label: 'Letzte 30 Tage' },
    { key: '90d', label: 'Letzte 90 Tage' },
    { key: 'all', label: 'Alle Zeit' }
  ];

  const filteredActivities = useMemo(() => {
    let filtered = [...activities];

    // Filter by type
    if (selectedFilter !== 'all') {
      switch (selectedFilter) {
        case 'login':
          filtered = filtered?.filter(a => a?.type === 'login' || a?.type === 'login_failed');
          break;
        case 'security':
          filtered = filtered?.filter(a => ['password_changed', '2fa_enabled', 'api_key_generated']?.includes(a?.type));
          break;
        case 'agents':
          filtered = filtered?.filter(a => ['agent_created', 'agent_deleted', 'agent_updated']?.includes(a?.type));
          break;
        case 'profile':
          filtered = filtered?.filter(a => ['profile_updated', 'email_changed']?.includes(a?.type));
          break;
      }
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(activity =>
        activity?.title?.toLowerCase()?.includes(searchLower) ||
        activity?.description?.toLowerCase()?.includes(searchLower)
      );
    }

    // Filter by date range
    if (dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch (dateRange) {
        case '24h':
          cutoff?.setHours(now?.getHours() - 24);
          break;
        case '7d':
          cutoff?.setDate(now?.getDate() - 7);
          break;
        case '30d':
          cutoff?.setDate(now?.getDate() - 30);
          break;
        case '90d':
          cutoff?.setDate(now?.getDate() - 90);
          break;
      }
      
      if (dateRange !== 'all') {
        filtered = filtered?.filter(activity => new Date(activity?.timestamp) >= cutoff);
      }
    }

    return filtered?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [activities, selectedFilter, searchTerm, dateRange]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Gerade eben';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Vor ${minutes} Minute${minutes > 1 ? 'n' : ''}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Vor ${hours} Stunde${hours > 1 ? 'n' : ''}`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      if (days < 7) {
        return `Vor ${days} Tag${days > 1 ? 'en' : ''}`;
      } else {
        return date?.toLocaleDateString('de-DE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-success bg-success/10 border-success/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'error':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const exportData = () => {
    const csvData = filteredActivities?.map(activity => ({
      Timestamp: new Date(activity?.timestamp)?.toLocaleString('de-DE'),
      Type: activity?.title,
      Description: activity?.description,
      Status: activity?.status
    }));
    
    const csvContent = "data:text/csv;charset=utf-8," +"Zeitstempel,Typ,Beschreibung,Status\n"
      + csvData?.map(row => Object.values(row)?.join(","))?.join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document?.createElement("a");
    link?.setAttribute("href", encodedUri);
    link?.setAttribute("download", `aktivitaeten_${user?.firstName}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Aktivitätshistorie</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Überblick über alle Aktivitäten in Ihrem Konto
          </p>
        </div>
        
        <Button
          onClick={exportData}
          variant="outline"
          iconName="Download"
          iconPosition="left"
          size="sm"
        >
          Exportieren
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Aktivitäten durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          
          {/* Date Range */}
          <div className="min-w-[200px]">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {dateRangeOptions?.map((option) => (
                <option key={option?.key} value={option?.key}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Activity Type Filters */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {activityFilters?.map((filter) => (
              <button
                key={filter?.key}
                onClick={() => setSelectedFilter(filter?.key)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                  selectedFilter === filter?.key
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
              >
                {filter?.label} ({filter?.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-card rounded-lg border border-border">
        {filteredActivities?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredActivities?.map((activity) => (
              <div key={activity?.id} className="p-6 hover:bg-muted/30 transition-colors">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center border flex-shrink-0",
                    getStatusColor(activity?.status)
                  )}>
                    <Icon name={activity?.icon} size={16} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-foreground mb-1">
                          {activity?.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {activity?.description}
                        </p>
                        
                        {/* Metadata */}
                        {Object.keys(activity?.metadata)?.length > 0 && (
                          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                            {activity?.metadata?.device && (
                              <div className="flex items-center gap-1">
                                <Icon name="Monitor" size={12} />
                                <span>{activity?.metadata?.device}</span>
                              </div>
                            )}
                            {activity?.metadata?.location && (
                              <div className="flex items-center gap-1">
                                <Icon name="MapPin" size={12} />
                                <span>{activity?.metadata?.location}</span>
                              </div>
                            )}
                            {activity?.metadata?.ip && (
                              <div className="flex items-center gap-1">
                                <Icon name="Wifi" size={12} />
                                <span>{activity?.metadata?.ip}</span>
                              </div>
                            )}
                            {activity?.metadata?.agentName && (
                              <div className="flex items-center gap-1">
                                <Icon name="Bot" size={12} />
                                <span>{activity?.metadata?.agentName}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Timestamp */}
                      <div className="text-xs text-muted-foreground text-right">
                        {formatTimestamp(activity?.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Keine Aktivitäten gefunden
            </h3>
            <p className="text-muted-foreground">
              {searchTerm || selectedFilter !== 'all' ?'Versuchen Sie es mit anderen Suchkriterien oder Filtern.' :'Ihre Kontoaktivitäten werden hier angezeigt.'}
            </p>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {filteredActivities?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Aktivitätsstatistiken</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {activities?.filter(a => a?.type === 'login')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Erfolgreiche Anmeldungen</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {activities?.filter(a => a?.status === 'warning' || a?.status === 'error')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Sicherheitsereignisse</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {activities?.filter(a => a?.type?.includes('agent'))?.length}
              </div>
              <div className="text-sm text-muted-foreground">Agent-Aktivitäten</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {Math.round((now - new Date(activities?.[activities?.length - 1]?.timestamp)) / (1000 * 60 * 60 * 24))}
              </div>
              <div className="text-sm text-muted-foreground">Tage aktiv</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityHistory;